import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("You're on the waitlist! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-32 bg-slate-50"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
            Get Started
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-primary tracking-tight uppercase mb-6">
            Ready to Save $2,500-$4,000/Month?
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto">
            Join our waitlist and we'll get you started within 24 hours.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl p-8 shadow-lg"
            data-testid="contact-form-container"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Full Name
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-14 text-lg border-slate-300 focus:border-primary focus:ring-primary"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-14 text-lg border-slate-300 focus:border-primary focus:ring-primary"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                  Phone Number
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-14 text-lg border-slate-300 focus:border-primary focus:ring-primary"
                  data-testid="contact-phone-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                  Message (Optional)
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your trucking operation..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="text-lg border-slate-300 focus:border-primary focus:ring-primary resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide py-6 text-lg rounded-md btn-primary"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Join The Waitlist
                  </>
                )}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Direct Contact Options */}
            <div className="bg-primary noise-overlay rounded-2xl p-8 relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-heading text-2xl font-bold text-white uppercase mb-6">
                  Or Reach Out Directly
                </h3>
                
                <div className="space-y-4">
                  <a
                    href="https://wa.me/15146993007"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors"
                    data-testid="whatsapp-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center whatsapp-pulse">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">WhatsApp</p>
                      <p className="text-slate-300">+1 (514) 699-3007</p>
                    </div>
                  </a>

                  <a
                    href="tel:+15146993007"
                    className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors"
                    data-testid="phone-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Call Us</p>
                      <p className="text-slate-300">+1 (514) 699-3007</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@mylane.io"
                    className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 hover:bg-white/20 transition-colors"
                    data-testid="email-link"
                  >
                    <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Email</p>
                      <p className="text-slate-300">info@mylane.io</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="font-heading text-xl font-bold text-primary uppercase mb-4">
                Why Drivers Trust MyLane
              </h3>
              <ul className="space-y-3">
                {[
                  "Save $2,500-$4,000 every month",
                  "Based in North America",
                  "No long-term contracts",
                  "Cancel anytime guarantee",
                  "Dedicated support team",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-600">
                    <div className="w-2 h-2 rounded-full bg-accent" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Response Time Badge */}
            <div className="bg-accent/10 rounded-xl p-6 text-center">
              <p className="text-accent font-bold text-lg">
                Average Response Time: <span className="text-2xl">15 minutes</span>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
